import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { css, keyframes } from '@emotion/css';
import type { TFunction } from 'i18next';

import { momentoDelDia, PALETAS } from '#components/manager/puerto/momento';
import { color, espacio } from '#components/mobile/ui/tokens';
import { useGlobalPref } from '#hooks/useGlobalPref';
import { useLocale } from '#hooks/useLocale';

/** Parte del día del saludo (no es la del cielo: en España «días» hasta comer). */
export type ParteDia = 'manana' | 'tarde' | 'noche';

/** Buenos días de 6 a 14, buenas tardes hasta las 21 y buenas noches el resto. */
export function parteDelDia(fecha: Date): ParteDia {
  const hora = fecha.getHours();
  if (hora >= 6 && hora < 14) {
    return 'manana';
  }
  if (hora >= 14 && hora < 21) {
    return 'tarde';
  }
  return 'noche';
}

/** «Buenos días, Vadik» (o «Buenos días» si no hay nombre). Sin género. */
export function textoSaludo(t: TFunction, parte: ParteDia, nombre?: string) {
  const limpio = nombre?.trim();
  if (limpio) {
    switch (parte) {
      case 'manana':
        return t('Good morning, {{name}}', { name: limpio });
      case 'tarde':
        return t('Good afternoon, {{name}}', { name: limpio });
      default:
        return t('Good evening, {{name}}', { name: limpio });
    }
  }
  switch (parte) {
    case 'manana':
      return t('Good morning');
    case 'tarde':
      return t('Good afternoon');
    default:
      return t('Good evening');
  }
}

const brillo = keyframes({ '50%': { opacity: 0.25 } });
const claseEstrella = css({
  position: 'absolute',
  width: 2,
  height: 2,
  borderRadius: '50%',
  backgroundColor: '#ffffff',
  animation: `${brillo} 3s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/** Posiciones fijas (no al azar: la cabecera no salta al volver a Inicio). */
const ESTRELLAS = Array.from({ length: 24 }, (_, i) => ({
  izquierda: `${(i * 37) % 100}%`,
  arriba: (i * 23) % 70,
  retraso: (i % 7) * 0.4,
  opacidad: 0.45 + (i % 3) * 0.2,
}));

/**
 * Cabecera de Inicio: el saludo según la hora, con el nombre de la Primera
 * travesía (`nombrePerfil`), sobre el mismo cielo del puerto (amanecer, día,
 * atardecer o noche) que se funde con el fondo. Encima, la fecha y el día
 * del mes; a la derecha, `derecha` (el ancla de la copia).
 */
export function SaludoCabecera({
  month,
  derecha,
}: {
  month: string;
  derecha?: ReactNode;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [nombre] = useGlobalPref('nombrePerfil');
  // La hora se fija al montar: la cabecera no cambia mientras la miras.
  const [ahora] = useState(() => new Date());
  const paleta = PALETAS[momentoDelDia(ahora)];
  const saludo = textoSaludo(t, parteDelDia(ahora), nombre);

  const dia = ahora.getDate();
  const diasMes = new Date(
    ahora.getFullYear(),
    ahora.getMonth() + 1,
    0,
  ).getDate();
  let fecha = '';
  try {
    fecha = new Intl.DateTimeFormat(locale?.code, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(ahora);
  } catch {
    // Locale raro: el mes a secas.
    fecha = monthUtils.format(month, 'MMMM', locale);
  }

  return (
    <View
      data-testid="inicio-saludo"
      style={{ position: 'relative', overflow: 'hidden', flexShrink: 0 }}
    >
      <View
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          // El cielo se funde con el fondo de la página al llegar abajo.
          background: `linear-gradient(to bottom, ${paleta.cielo[0]} 0%, ${paleta.cielo[1]} 58%, ${color.bg} 100%)`,
        }}
      >
        {paleta.estrellas > 0 &&
          ESTRELLAS.slice(0, Math.round(paleta.estrellas * 40)).map((e, i) => (
            <span
              key={i}
              className={claseEstrella}
              style={{
                left: e.izquierda,
                top: e.arriba + 6,
                opacity: e.opacidad,
                animationDelay: `${e.retraso}s`,
              }}
            />
          ))}
        <span
          style={{
            position: 'absolute',
            right: 78,
            top: 18,
            width: 26,
            height: 26,
            borderRadius: '50%',
            backgroundColor: paleta.colorSol,
            boxShadow:
              paleta.astro === 'luna'
                ? `inset -7px -3px 0 0 ${paleta.cielo[0]}`
                : `0 0 22px ${paleta.colorSol}`,
            opacity: paleta.astro === 'luna' ? 0.9 : 1,
          }}
        />
      </View>
      <View
        style={{
          position: 'relative',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 12,
          paddingTop: 10,
          paddingLeft: espacio.margen,
          paddingRight: espacio.margen,
          paddingBottom: 44,
        }}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: paleta.subtitulo,
              display: 'block',
              '&::first-letter': { textTransform: 'uppercase' },
            }}
          >
            <span data-testid="inicio-mes">{fecha}</span>
            {' · '}
            {t('day {{day}} of {{days}}', { day: dia, days: diasMes })}
          </Text>
          <Text
            role="heading"
            aria-level={1}
            data-testid="inicio-saludo-texto"
            style={{
              fontSize: 26,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.12,
              marginTop: 3,
              color: paleta.titulo,
              overflowWrap: 'anywhere',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textShadow: '0 1px 8px rgba(0, 0, 0, 0.18)',
            }}
          >
            {saludo}
          </Text>
        </View>
        {derecha}
      </View>
    </View>
  );
}
